import { graphql, type HeadProps, type PageProps } from 'gatsby'
import React from 'react'

import AgendaBlock from '@/components/blocks-library/agenda/agenda-block'
import FeaturedCta from '@/components/blocks-library/featured-cta/featured-cta'
import FeaturedStatic from '@/components/blocks-library/featured-static/featured-static'
import Logos, { Logo } from '@/components/blocks-library/logos/logos'
import MediaGalleryWrapper from '@/components/blocks-library/media-gallery/media-gallery'
import HeaderBackgroundV4 from '@/components/header/header-background-v4'
import MenuPage from '@/components/navigation/menu-page'
import NavigableSection from '@/components/navigation/navigable-section'
import Related from '@/components/related/related'
import { Microdata } from '@/components/seo/microdata'
import Seo from '@/components/seo/seo'
import routes from '@/internal/constants/routes'
import Layout from '@/layouts/layout'
import { anchorFormatted } from '@/utils/format'
import { NavigationItem } from '@/utils/get-navigation-label'
import { sortRelatedContent } from '@/utils/sort'

export type DataProps = {
  datoCmsPctEvent: Queries.EventQuery['datoCmsPctEvent']
  allDatoCmsPctEvent: Queries.EventQuery['allDatoCmsPctEvent']
  allDatoCmsPctEventOther: Queries.EventQuery['allDatoCmsPctEventOther']
  allDatoCmsPctPaper: Queries.EventQuery['allDatoCmsPctPaper']
  allDatoCmsPctPost: Queries.EventQuery['allDatoCmsPctPost']
  allDatoCmsPctInterview: Queries.EventQuery['allDatoCmsPctInterview']
  allDatoCmsPctDialog: Queries.EventQuery['allDatoCmsPctDialog']
  allDatoCmsPctProject: Queries.EventQuery['allDatoCmsPctProject']
  allDatoCmsPctEpisode: Queries.EventQuery['allDatoCmsPctEpisode']
  allDatoCmsPctPage: Queries.EventQuery['allDatoCmsPctPage']
  allDatoCmsPctExternalContent: Queries.EventQuery['allDatoCmsPctExternalContent']
}
const menuLabels = {
  theEvent: 'The Event',
  agenda: 'Agenda',
  partners: 'Partners',
  register: 'Register',
  related: 'Related',
  other: 'Latest Events'
}
export const Head: React.FC<HeadProps<DataProps>> = ({
  data: { datoCmsPctEvent },
  location
}) => {
  return (
    <>
      <Seo data={datoCmsPctEvent} location={location} />
      <Microdata data={datoCmsPctEvent} location={location} />
    </>
  )
}
const Event: React.FC<PageProps<DataProps>> = ({ data, location }) => {
  if (!data.datoCmsPctEvent) return null
  const {
    externalName,
    image,
    excerpt,
    introTitle,
    intro,
    logos,
    agenda,
    partners,
    registrationUrl,
    registrationTitle,
    registrationDescription,
    location: eventLocation,
    eventName,
    dateInitialMonth,
    dateInitialYear,
    dateInitialDay,
    dateEndDay,
    promotionalMedia,
    gallery
  } = data.datoCmsPctEvent
  const related = getRelatedContent(data)
  const otherEvents = getOtherEvents(data)
  const sameDay = dateInitialDay === dateEndDay || dateEndDay === null
  const days = sameDay ? dateInitialDay : `${dateInitialDay} - ${dateEndDay}`
  const headline = `${eventLocation} | ${days} ${dateInitialMonth} ${dateInitialYear}`

  // Create registration CTA
  const ctaRegister = createRegistrationCta(registrationUrl)
  const introImage = getIntroImage(promotionalMedia)
  const introVideo = getIntroVideo(promotionalMedia)
  const menu = generateInternalMenu(data?.datoCmsPctEvent)

  // Determine which sections exist
  const hasAgenda = agenda !== null && agenda !== undefined && agenda.length > 0
  const hasGallery =
    gallery !== null && gallery !== undefined && gallery.length > 0
  const hasPartners =
    partners !== null && partners !== undefined && partners.length > 0
  const hasRegister = !!ctaRegister
  return (
    <Layout location={location} shareButtonsScrollTop={150}>
      <HeaderBackgroundV4
        headline={externalName ?? ''}
        image={image}
        pretitle={'NTT DATA Insurance Latest Events'}
        pretitleLink={routes.eventsRoute}
        date={headline ?? ''}
        description={excerpt ?? ''}
      />
      <MenuPage blocks={menu} />
      <NavigableSection
        id={anchorFormatted(menuLabels.theEvent)}
        isFirst={true}
      >
        <FeaturedStatic
          __typename="DatoCmsFeaturedStatic"
          content={
            intro as unknown as Queries.FeaturedStaticFragment['content']
          }
          ctas={[ctaRegister]}
          fullImage={introImage as Queries.FeaturedStaticFragment['fullImage']}
          image={introImage as Queries.FeaturedStaticFragment['image']}
          hideImageResponsive={false}
          id=""
          mediaViewInModal={false}
          navigationLabel={null}
          reverse={true}
          subtitle={introTitle}
          title={`<span>THE EVENT</span> / ${eventName}`}
          video={introVideo}
          oneColumn={!!introVideo}
          oneColumnCentered={!!introVideo}
          widthFirstColumn={'6'}
        />
      </NavigableSection>
      {logos !== null && (
        <Logos
          logos={logos as unknown as Logo[]}
          size="big"
          grayscale={false}
        />
      )}
      {hasAgenda && (
        <NavigableSection
          id={anchorFormatted(menuLabels.agenda)}
          isFirst={false}
          componentTypeForCSS="agenda"
        >
          <AgendaBlock
            agenda={agenda as unknown as Queries.AgendaBlockFragment['agenda']}
            title={`${days} ${eventLocation}`}
            __typename="DatoCmsAgendaBlock"
            id=""
            navigationLabel={null}
          />
        </NavigableSection>
      )}
      {hasGallery &&
        gallery.map((item, index) => {
          if (item?.items === null || item?.items === undefined) return <></>
          return (
            <NavigableSection
              key={item?.id}
              id={anchorFormatted(item.title ?? '')}
              isFirst={false}
            >
              {' '}
              <MediaGalleryWrapper
                __typename="DatoCmsMediaGallery"
                id={item?.id}
                title={item?.title ?? ''}
                items={item?.items}
              />
            </NavigableSection>
          )
        })}
      {hasPartners && (
        <NavigableSection
          id={anchorFormatted(menuLabels.partners)}
          isFirst={false}
          componentTypeForCSS="logos"
        >
          <Logos
            logos={partners as unknown as Logo[]}
            size="small"
            grayscale={false}
            title="PARTNERS"
          />
        </NavigableSection>
      )}
      {hasRegister && (
        <NavigableSection
          id={anchorFormatted(menuLabels.register)}
          isFirst={false}
        >
          <FeaturedCta
            __typename="DatoCmsFeaturedCta"
            cta={ctaRegister}
            id=""
            navigationLabel={null}
            subtitle={
              registrationDescription !== '' && registrationDescription !== null
                ? registrationDescription
                : 'Join us for our sessions by registering today. Register today and secure your spot for an unforgettable experience!'
            }
            title={
              registrationTitle !== '' && registrationTitle !== null
                ? registrationTitle
                : `Register to ${eventName}`
            }
            width={'medium'}
          />
        </NavigableSection>
      )}
      <NavigableSection
        id={anchorFormatted(menuLabels.related)}
        isFirst={false}
      >
        <Related related={related} />
      </NavigableSection>

      <NavigableSection id={anchorFormatted(menuLabels.other)}>
        <Related related={otherEvents} title="Latest events" />
      </NavigableSection>
    </Layout>
  )
}

const getRelatedContent = (data: Queries.EventQuery) => {
  const nodesGroups = [
    [
      ...(data.allDatoCmsPctEvent?.nodes ?? []),
      ...(data.allDatoCmsPctPaper?.nodes ?? []),
      ...(data.allDatoCmsPctProject?.nodes ?? []),
      ...(data.allDatoCmsPctPost?.nodes ?? []),
      ...(data.allDatoCmsPctInterview?.nodes ?? []),
      ...(data.allDatoCmsPctDialog?.nodes ?? []),
      ...(data.allDatoCmsPctEpisode?.nodes ?? []),
      ...(data.allDatoCmsPctPage?.nodes ?? []),
      ...(data.allDatoCmsPctExternalContent?.nodes ?? [])
    ]
  ]
  return sortRelatedContent({ results: 3, nodesGroups })
}

const getOtherEvents = (data: Queries.EventQuery) => {
  const nodesGroups = [data.allDatoCmsPctEventOther?.nodes as Card[]]
  return sortRelatedContent({ results: 3, nodesGroups })
}

const createRegistrationCta = (registrationUrl: string | null) => {
  if (!registrationUrl) return null
  return {
    __typename: 'DatoCmsCta',
    id: 'cta',
    navigationLabel: null,
    text: 'Register',
    ctaType: 'url',
    link: registrationUrl
  } as unknown as Queries.FeaturedCtaFragment['cta']
}

const getIntroImage = (
  promotionalMedia: Queries.EventPromotionalMediaFragment | null
) => {
  if (promotionalMedia?.__typename === 'DatoCmsImage') {
    return promotionalMedia.image
  }
  if (promotionalMedia?.__typename === 'DatoCmsVideo') {
    return promotionalMedia.preview
  }
  return null
}

const getIntroVideo = (
  promotionalMedia: Queries.EventPromotionalMediaFragment | null
) => {
  if (promotionalMedia?.__typename !== 'DatoCmsVideo') {
    return null
  }
  return promotionalMedia.video
}

const generateInternalMenu = (data: Queries.EventQuery['datoCmsPctEvent']) => {
  const menu = new Set<NavigationItem>()

  menu.add({
    navigationLabel: menuLabels.theEvent
  })

  if (
    data?.agenda !== null &&
    data?.agenda !== undefined &&
    data?.agenda.length > 0
  ) {
    menu.add({
      navigationLabel: menuLabels.agenda
    })
  }

  if (
    data?.gallery !== null &&
    data?.gallery !== undefined &&
    data?.gallery.length > 0
  ) {
    data.gallery.map((item) => {
      menu.add({
        navigationLabel: item?.title
      })
    })
  }

  if (
    data?.partners !== null &&
    data?.partners !== undefined &&
    data?.partners.length > 0
  ) {
    menu.add({
      navigationLabel: menuLabels.partners
    })
  }

  if (data?.registrationUrl) {
    menu.add({
      navigationLabel: menuLabels.register
    })
  }

  menu.add({
    navigationLabel: menuLabels.related
  })

  menu.add({
    navigationLabel: menuLabels.other
  })

  return [...menu]
}

export default Event

export const query = graphql`
  query Event($id: String, $related: [String] = "") {
    datoCmsPctEvent(id: { eq: $id }) {
      ...PctEventDetail
    }
    allDatoCmsPctEventOther: allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { ne: $id } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
    allDatoCmsPctEvent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEventCard
      }
    }
    allDatoCmsPctPaper(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPaperCard
      }
    }
    allDatoCmsPctPost(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPostCard
      }
    }
    allDatoCmsPctPage(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctPageCard
      }
    }
    allDatoCmsPctInterview(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctInterviewCard
      }
    }
    allDatoCmsPctDialog(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctDialogCard
      }
    }
    allDatoCmsPctProject(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctProjectCard
      }
    }
    allDatoCmsPctEpisode(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctEpisodeCard
      }
    }

    allDatoCmsPctExternalContent(
      sort: { meta: { firstPublishedAt: DESC } }
      limit: 3
      filter: { id: { in: $related } }
    ) {
      nodes {
        ...PctExternalContentCard
      }
    }
  }

  fragment PctEventDetail on DatoCmsPctEvent {
    __typename
    originalId
    seoMetaTags {
      ...GatsbyDatoCmsSeoMetaTags
    }
    seo {
      title
      description
      image {
        url
      }
    }
    meta {
      firstPublishedAt
      publishedAtTimestamp: publishedAt(formatString: "X")
    }
    id
    externalName
    excerpt
    location
    eventName
    isPreEvent
    redirectToRegistration
    registrationUrl
    registrationTitle
    registrationDescription
    dateInitial
    dateInitialMonth: dateInitial(formatString: "MMMM")
    dateInitialDay: dateInitial(formatString: "Do")
    dateInitialYear: dateInitial(formatString: "YYYY")
    dateEndMonth: dateEnd(formatString: "MMMM")
    dateEndDay: dateEnd(formatString: "Do")
    partners {
      originalId
      gatsbyImageData(height: 200)
      title
      alt
      customData
      height
      width
    }
    logos {
      originalId
      gatsbyImageData(height: 200)
      title
      alt
      customData
      height
      width
    }
    image {
      gatsbyImageData(
        layout: FIXED
        imgixParams: { w: "1500", h: "750", fit: "crop", crop: "focalpoint" }
      )
      alt
      author
      customData
      title
    }
    topic {
      ...TopicReference
    }
    tags {
      ...TagReference
    }
    introTitle
    intro {
      value
      blocks {
        __typename
        ... on DatoCmsCta {
          # All Blocks insert in a structured text needs to alias originalId to id
          id: originalId
          ...Cta
        }
      }
      links {
        __typename
        ... on DatoCmsPctLink {
          ...PctLink
        }
      }
    }
    promotionalMedia {
      ...EventPromotionalMedia
    }
    gallery {
      ...MediaGallery
    }
    agenda {
      title
      items {
        ... on DatoCmsAgendaItem {
          ...AgendaItem
        }
      }
    }
  }
  fragment EventPromotionalMedia on DatoCmsUnionForDatoCmsPctEventPromotionalMedia {
    __typename
    ... on DatoCmsVideo {
      video {
        url
        thumbnailUrl
        title
      }
      preview {
        alt
        gatsbyImageData(imgixParams: { w: "1200" })
        fluid(imgixParams: { w: "1200" }) {
          src
          height
          width
        }
      }
    }
    ... on DatoCmsImage {
      image {
        alt
        originalId
        gatsbyImageData(imgixParams: { w: "1200" })
        fluid(imgixParams: { w: "1200" }) {
          src
          height
          width
        }
      }
    }
  }
`
